import React, { useEffect, useRef, useMemo, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChatMessage } from '../types';

interface ChatDisplayProps {
  chatHistory: ChatMessage[];
  isTypingIntroduction?: boolean;
  isLoading?: boolean;
  isVoiceProcessing?: boolean;
}

const ChatDisplay: React.FC<ChatDisplayProps> = ({ chatHistory, isTypingIntroduction = false, isLoading = false, isVoiceProcessing = false }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  }, [chatHistory]);

  // Memoize the renderMessageContent function
  const renderMessageContent = useCallback((message: ChatMessage) => {
    const content = message.parts[0]?.text || '';
    
    // Render markdown for system messages (model role), plain text for user messages
    if (message.role === 'model') {
      return (
        <div className="prose prose-sm max-w-none">
          <ReactMarkdown 
            remarkPlugins={[remarkGfm]}
            components={{
              p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
              h1: ({ children }) => <h1 className="text-lg font-bold mb-2">{children}</h1>,
              h2: ({ children }) => <h2 className="text-base font-semibold mb-2">{children}</h2>,
              h3: ({ children }) => <h3 className="text-sm font-semibold mb-1">{children}</h3>,
              ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
              ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
              li: ({ children }) => <li className="text-sm">{children}</li>,
              blockquote: ({ children }) => (
                <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-600 mb-2">
                  {children}
                </blockquote>
              ),
              code: ({ children, className }) => {
                const isInline = !className;
                if (isInline) {
                  return <code className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">{children}</code>;
                }
                return (
                  <pre className="bg-gray-100 p-2 rounded text-sm font-mono overflow-x-auto mb-2">
                    <code>{children}</code>
                  </pre>
                );
              },
              strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
              em: ({ children }) => <em className="italic">{children}</em>,
              a: ({ href, children }) => (
                <a href={href} className="text-blue-600 hover:text-blue-800 underline" target="_blank" rel="noopener noreferrer">
                  {children}
                </a>
              ),
            }}
          >
            {content}
          </ReactMarkdown>
        </div>
      );
    } else {
      // Plain text for user messages
      return <p className="text-sm">{content}</p>;
    }
  }, []);

  // Memoize the rendered messages to prevent unnecessary re-renders
  const renderedMessages = useMemo(() => {
    return chatHistory.map((msg, idx) => (
      <div key={`${msg.role}-${idx}-${msg.parts[0]?.text?.slice(0, 20)}`} className={`mb-4 flex ${msg.role === 'user' || msg.role === 'live_transcription' ? 'justify-end' : 'justify-start'}`}>
        <div className={`px-4 py-2 rounded-lg max-w-xs lg:max-w-md ${
          msg.role === 'user' || msg.role === 'live_transcription'
            ? 'bg-gray-800 text-white' 
            : 'bg-white border border-gray-100 text-gray-900'
        }`}>
          {renderMessageContent(msg)}
        </div>
      </div>
    ));
  }, [chatHistory, renderMessageContent]);

  return (
    <div 
      ref={scrollContainerRef}
      className="overflow-y-auto mb-4 bg-gray-50 rounded p-3 h-full"
    >
      {renderedMessages}
      
      {/* Thinking Indicator */}
      {(isLoading || isVoiceProcessing) && (
        <div className="mb-4 flex justify-start">
          <div className="px-4 py-4 rounded-lg max-w-[80%] text-sm bg-white border border-gray-100 text-gray-900">
            <div className="flex items-center space-x-2 mb-1">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
              <span className="text-xs font-medium text-blue-700">
                {isVoiceProcessing ? 'Thinking' : 'I\'m thinking...'}
              </span>
            </div>
            <div className="flex space-x-1">
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatDisplay; 